import { createReducer, on, props } from '@ngrx/store';
import moment from 'moment';
import {
  IAvailableJobColumn,
  IAvailableJob,
  ScheduledJobGridItem,
  IScheduledJobColumn,
  ScheduledJobColumnFieldName,
  RangeValue,
} from 'src/app/core/dto';

import { AvailableJobGridItem } from './selectors';

import * as schedulerActions from './actions';
import { JOB_ROLE, MATERIAL_ROLE, TOOLING_ROLE } from 'src/app/core/scheduler-roles';

const isDateString: (string: any) => boolean = (str) => !isNaN(Date.parse(str));

// Role palette lives in `src/app/core/scheduler-roles.ts`. These aliases keep the
// column definitions below reading the way they always have.
const jobRole = JOB_ROLE;
const materialRole = MATERIAL_ROLE;
const toolingRole = TOOLING_ROLE;

const onlyUnique = (value, index, self) => self.indexOf(value) === index;

const appendSummarizer = (jobs: AvailableJobGridItem[], column: IAvailableJobColumn) => {
  const set = jobs
    .map((job) => job[column.fieldName].toString())
    // TODO: Use [...new Set(...)] when we go to ES6 target
    .filter(onlyUnique);

  if (set.length === 1) return set[0];

  return set.reduce((acc, val) => acc + ', ' + val).substr(0, 15) + `... (${set.length} items)`;
};

export const appendSummarizer2 = (
  jobs: ScheduledJobGridItem[],
  column: { fieldName: ScheduledJobColumnFieldName }
) => {
  const set = jobs
    .map((job) => job[column.fieldName].toString())
    // TODO: Use [...new Set(...)] when we go to ES6 target
    .filter(onlyUnique);

  if (set.length === 1) return set[0];

  return set.reduce((acc, val) => acc + ', ' + val).substr(0, 15) + `... (${set.length} items)`;
};

const countSummarizer2 = (
  jobs: ScheduledJobGridItem[],
  column: { fieldName: ScheduledJobColumnFieldName },
  suffix = 'item'
) => {
  const set = jobs
    .map((job) => job[column.fieldName].toString())
    // TODO: Use [...new Set(...)] when we go to ES6 target
    .filter(onlyUnique);

  return `${set.length} ${suffix}` + (set.length > 1 ? 's' : '');
};
const rangeSummarizer = (filter?: (str: string) => boolean) => {
  const summarizer = (jobs: AvailableJobGridItem[], column: IAvailableJobColumn) => {
    if (jobs.length === 1) return jobs[0][column.fieldName].toString();

    const sortedCopy = jobs
      .map((x) => x[column.fieldName].toString())
      .filter((x) => filter === undefined || filter(x));

    sortedCopy.sort();

    if (sortedCopy.length > 1) return `${sortedCopy[0]} - ${sortedCopy[sortedCopy.length - 1]}`;
    else if (sortedCopy.length === 1) return `${sortedCopy[0]}`;
    else return '<none>';
  };

  return summarizer;
};
const rangeSummarizer2 = (filter?: (str: string) => boolean) => {
  function summarizer(
    jobs: ScheduledJobGridItem[],
    column: { fieldName: ScheduledJobColumnFieldName }
  ): string | RangeValue {
    if (jobs.length === 1) return jobs[0][column.fieldName].toString();

    const sortedCopy = jobs
      .map((x) => x[column.fieldName].toString())
      .filter((x) => filter === undefined || filter(x));

    const by = (selector) => (e1, e2) => selector(e1) > selector(e2) ? 1 : -1;
    sortedCopy.sort(by((x) => x[column.fieldName]));

    if (sortedCopy.length > 1)
      return {
        start: sortedCopy[0],
        end: sortedCopy[sortedCopy.length - 1],
      };
    else if (sortedCopy.length === 1) return `${sortedCopy[0]}`;
    else return '<none>';
  }

  return summarizer;
};

const sumSummarizer = (jobs: AvailableJobGridItem[], column: IAvailableJobColumn) => {
  return jobs
    .map((j) => Number(j[column.fieldName]))
    .reduce((sum, n) => sum + n, 0)
    .toString();
};
const sumSummarizer2 = (
  jobs: ScheduledJobGridItem[],
  column: { fieldName: ScheduledJobColumnFieldName }
) =>
  jobs
    .map((j) => Number(j[column.fieldName]))
    .reduce((sum, n) => sum + n, 0)
    .toString();
const timespanSummarizer2 = (
  jobs: ScheduledJobGridItem[],
  column: { fieldName: ScheduledJobColumnFieldName }
) =>
  jobs
    .map((j) => moment.duration(j[column.fieldName]))
    .reduce((acc, cur) => acc.add(cur))
    .toISOString();

// Load saved column preferences from localStorage
const loadAvailableColumnPreferences = (): { visibility?: Record<string, boolean>, order?: string[] } | null => {
  try {
    const saved = localStorage.getItem('availableJobColumns');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Handle both old format (just visibility) and new format (visibility + order)
    if (typeof parsed === 'object' && !Array.isArray(parsed) && !parsed.visibility) {
      // Old format - just visibility preferences
      return { visibility: parsed, order: null };
    }
    return parsed;
  } catch {
    return null;
  }
};

// Save column preferences to localStorage (including order)
const saveAvailableColumnPreferences = (columns: IAvailableJobColumn[]) => {
  try {
    // Save both visibility and order
    const preferences = {
      visibility: columns.reduce((acc, col) => {
        acc[col.fieldName] = col.isChecked;
        return acc;
      }, {} as Record<string, boolean>),
      order: columns.map(col => col.fieldName)
    };
    localStorage.setItem('availableJobColumns', JSON.stringify(preferences));
  } catch {
    // Ignore errors
  }
};

// Create the initial column state without saved preferences
const baseAvailableColumnState: IAvailableJobColumn[] = [
  {
    fieldName: 'toolingCode',
    displayName: 'tooling',
    name: 'Tooling',
    isChecked: true,
    role: toolingRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'materialCode',
    displayName: 'material',
    name: 'Material',
    isChecked: true,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'requiredDateDisplay',
    displayName: 'requiredBy',
    name: 'Required By',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer(isDateString),
  },
  {
    fieldName: 'orderCode',
    displayName: 'order',
    name: 'Order',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'customerName',
    displayName: 'customer',
    name: 'Customer',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'remainingFt',
    displayName: 'length',
    name: 'Length',
    isChecked: true,
    role: jobRole,
    units: 'ft',
    summarizer: sumSummarizer,
  },
  {
    fieldName: 'totalFt',
    displayName: 'total',
    name: 'Total',
    isChecked: false,
    role: jobRole,
    units: 'ft',
    summarizer: sumSummarizer,
  },
  {
    fieldName: 'remainingPieces',
    displayName: 'pieces',
    name: 'Pieces',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: sumSummarizer,
  },
  {
    fieldName: 'totalPieces',
    displayName: 'totalPieces',
    name: 'Total Pieces',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: sumSummarizer,
  },
  {
    fieldName: 'truckNumber',
    displayName: 'truck',
    name: 'Truck',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'stagingBay',
    displayName: 'stagingBay',
    name: 'Staging Bay',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'loadingDock',
    displayName: 'loadingDock',
    name: 'Loading Dock',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'materialColor',
    displayName: 'color',
    name: 'Color',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'materialGauge',
    displayName: 'gauge',
    name: 'Gauge',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: rangeSummarizer(),
  },
  {
    fieldName: 'materialWidthIn',
    displayName: 'width',
    name: 'Width',
    isChecked: false,
    role: materialRole,
    units: 'in',
    summarizer: rangeSummarizer(),
  },
  {
    fieldName: 'materialDescription',
    displayName: 'materialDescription',
    name: 'Material Description',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'toolingDescription',
    displayName: 'toolingDescription',
    name: 'Tooling Description',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'longestLengthIn',
    displayName: 'longestPart',
    name: 'Longest Part',
    isChecked: false,
    role: jobRole,
    units: 'in',
    summarizer: rangeSummarizer(),
  },
  {
    fieldName: 'salesOrder',
    displayName: 'so',
    name: 'SO',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'user1',
    displayName: 'orderUser1',
    name: 'User1',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  }, //
  {
    fieldName: 'user2',
    displayName: 'orderUser2',
    name: 'User2',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'user3',
    displayName: 'orderUser3',
    name: 'User3',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'user4',
    displayName: 'orderUser4',
    name: 'User4',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'user5',
    displayName: 'orderUser5',
    name: 'User5',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'workOrder',
    displayName: 'Work Order',
    name: 'Work Order',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer,
  },
  {
    fieldName: 'importDateDisplay',
    displayName: 'imported',
    name: 'Imported',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer(isDateString),
  },
  {
    fieldName: 'shipDateDisplay',
    displayName: 'shipDate',
    name: 'Ship Date',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer(isDateString),
  },
];

export const initialScheduledJobColumnState: IScheduledJobColumn[] = [
  {
    fieldName: 'toolingCode',
    displayName: 'tooling',
    name: 'Tooling',
    isChecked: true,
    role: toolingRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'materialCode',
    displayName: 'material',
    name: 'Material',
    isChecked: true,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'requiredDateDisplay',
    displayName: 'requiredBy',
    name: 'Required By',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer2(isDateString),
  },
  {
    fieldName: 'orderCode',
    displayName: 'order',
    name: 'Order',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'customerName',
    displayName: 'customer',
    name: 'Customer',
    isChecked: true,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'totalFt',
    displayName: 'total',
    name: 'Length',
    isChecked: false,
    role: jobRole,
    units: 'ft',
    summarizer: sumSummarizer2,
  },
  {
    fieldName: 'remainingFt',
    displayName: 'remaining',
    name: 'RemainingLength',
    isChecked: true,
    role: jobRole,
    units: 'ft',
    summarizer: sumSummarizer2,
  },
  {
    fieldName: 'remainingPieces',
    displayName: 'pieces',
    name: 'Pieces',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: sumSummarizer2,
  },
  {
    fieldName: 'quantity',
    displayName: 'totalPieces',
    name: 'Total Pieces',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: sumSummarizer2,
  },
  {
    fieldName: 'truckNumber',
    displayName: 'truck',
    name: 'Truck',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'stagingBay',
    displayName: 'stagingBay',
    name: 'Staging Bay',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'loadingDock',
    displayName: 'loadingDock',
    name: 'Loading Dock',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'materialColor',
    displayName: 'color',
    name: 'Color',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'materialGauge',
    displayName: 'gauge',
    name: 'Gauge',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'materialWidthIn',
    displayName: 'width',
    name: 'Width',
    isChecked: false,
    role: materialRole,
    units: 'in',
    summarizer: rangeSummarizer2(),
  },
  {
    fieldName: 'materialDescription',
    displayName: 'materialDescription',
    name: 'Material Description',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'toolingDescription',
    displayName: 'toolingDescription',
    name: 'Tooling Description',
    isChecked: false,
    role: materialRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'longestLengthIn',
    displayName: 'longestPart',
    name: 'Longest Part',
    isChecked: false,
    role: jobRole,
    units: 'in',
    summarizer: rangeSummarizer2(),
  },
  {
    fieldName: 'salesOrder',
    displayName: 'so',
    name: 'SO',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'user1',
    displayName: 'orderUser1',
    name: 'User1',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  }, //
  {
    fieldName: 'user2',
    displayName: 'orderUser2',
    name: 'User2',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'user3',
    displayName: 'orderUser3',
    name: 'User3',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'user4',
    displayName: 'orderUser4',
    name: 'User4',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'user5',
    displayName: 'orderUser5',
    name: 'User5',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'workOrder',
    displayName: 'Work Order',
    name: 'Work Order',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: appendSummarizer2,
  },
  {
    fieldName: 'importDateDisplay',
    displayName: 'imported',
    name: 'Imported',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer2(isDateString),
  },
  {
    fieldName: 'shipDateDisplay',
    displayName: 'shipDate',
    name: 'Ship Date',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer2(isDateString),
  },
  {
    fieldName: 'sequence',
    displayName: 'sequence',
    name: 'Sequence',
    isChecked: false,
    role: jobRole,
    units: '',
    summarizer: rangeSummarizer2(),
  },
  {
    fieldName: 'completePct',
    displayName: 'complete',
    name: 'CompletePct',
    isChecked: false,
    role: jobRole,
    units: '%',
    summarizer: appendSummarizer2, // todo: it would be nice to show the actual complete percentage of the group, but that could be quite difficult.
  },
];

// Apply saved preferences to create the initial state
export const initialAvailableColumnState: IAvailableJobColumn[] = (() => {
  const savedPrefs = loadAvailableColumnPreferences();
  if (!savedPrefs) {
    return baseAvailableColumnState;
  }

  const savedVisibility = savedPrefs.visibility || savedPrefs; // Handle both old and new format
  const savedOrder = savedPrefs.order;

  // Apply saved visibility preferences to each column
  let result = baseAvailableColumnState.map(col => ({
    ...col,
    isChecked: savedVisibility[col.fieldName] !== undefined
      ? savedVisibility[col.fieldName]
      : col.isChecked
  }));

  // Apply saved order if available
  if (savedOrder && savedOrder.length > 0) {
    result = result.sort((a, b) => {
      const aIndex = savedOrder.indexOf(a.fieldName);
      const bIndex = savedOrder.indexOf(b.fieldName);
      // If both are in saved order, sort by saved order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one is in saved order, it comes first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // Otherwise, keep original order
      return baseAvailableColumnState.indexOf(a) - baseAvailableColumnState.indexOf(b);
    });
  }

  return result;
})();

export const availableColumnsReducer = createReducer(
  initialAvailableColumnState,

  on(schedulerActions.initAvaColumnsAction, (state, { columns }) => {
    if (columns.length > 0) {
      // Check if we have localStorage preferences
      const savedPrefs = loadAvailableColumnPreferences();
      const savedVisibility = savedPrefs?.visibility;
      const savedOrder = savedPrefs?.order;

      let result = initialAvailableColumnState
        .map((col, i) => {
          const pos = columns.findIndex((u) => u.fieldName === col.fieldName);
          return {
            ...col,
            // Use localStorage preference if available, otherwise use server value
            isChecked: savedVisibility && col.fieldName in savedVisibility
              ? savedVisibility[col.fieldName]
              : (pos > -1 ? columns[pos].isChecked : col.isChecked),
            // ...and column order.
            position: pos > -1 ? pos : i + initialAvailableColumnState.length,
            width: pos > -1 ? columns[pos].width : -1, // todo: what is a good default?
          };
        });

      // If we have a saved order, apply it
      if (savedOrder && savedOrder.length > 0) {
        result = result.sort((a, b) => {
          const aIndex = savedOrder.indexOf(a.fieldName);
          const bIndex = savedOrder.indexOf(b.fieldName);
          // If both are in saved order, sort by saved order
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only one is in saved order, it comes first
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          // Otherwise, sort by position
          return a.position - b.position;
        });
      } else {
        result = result.sort((a, b) => (a.position > b.position ? 1 : -1));
      }

      return result;
    } else {
      return [...initialAvailableColumnState];
    }
  }),
  on(schedulerActions.toggleAvailableJobColumnAction, (state, { fieldName }) => {
    const newState = state.map((column) => {
      if (column.fieldName === fieldName) {
        return { ...column, isChecked: !column.isChecked };
      }
      return column;
    });
    // Save updated preferences to localStorage
    saveAvailableColumnPreferences(newState);
    return newState;
  }),
  on(schedulerActions.reorderAvailableColumnAction, (state, { fieldName, position }) => {
    const from = state.findIndex((x) => x.fieldName === fieldName);
    if (from > -1) {
      const to = position;

      // Move from one position to another, shifting everything
      // as necessary.
      const copy = [...state];
      copy.splice(to, 0, copy.splice(from, 1)[0]);

      // Save the reordered state to localStorage
      saveAvailableColumnPreferences(copy);
      return copy;
    }
    return state;
  }),

  on(schedulerActions.resetAvailableJobColumnsAction, (state) => {
    // Clear saved preferences when resetting
    try {
      localStorage.removeItem('availableJobColumns');
    } catch {
      // Ignore errors
    }
    // Return a fresh copy of the pristine base column definitions. Using
    // `baseAvailableColumnState` (not `initialAvailableColumnState`) ensures
    // both the order and the `isChecked` defaults come from the array literal,
    // not from a saved-order snapshot captured at module-load time.
    return baseAvailableColumnState.map(col => ({ ...col }));
  }),
  on(schedulerActions.changeWidthAvailableJobColumnAction, (state, { fieldName, width }) =>
    state.map((column) => {
      if (column.fieldName === fieldName) {
        return { ...column, width };
      }
      return column;
    })
  )
);

// Helper functions for localStorage persistence of assigned job columns
const loadAssignedColumnPreferences = (): { visibility?: Record<string, boolean>, order?: string[] } | null => {
  try {
    const saved = localStorage.getItem('assignedJobColumns');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Handle both old format (just visibility) and new format (visibility + order)
    if (typeof parsed === 'object' && !Array.isArray(parsed) && !parsed.visibility) {
      // Old format - just visibility preferences
      return { visibility: parsed, order: null };
    }
    return parsed;
  } catch {
    return null;
  }
};

const saveAssignedColumnPreferences = (columns: IScheduledJobColumn[]) => {
  try {
    // Save both visibility and order
    const preferences = {
      visibility: columns.reduce((acc, col) => {
        acc[col.fieldName] = col.isChecked;
        return acc;
      }, {} as Record<string, boolean>),
      order: columns.map(col => col.fieldName)
    };
    localStorage.setItem('assignedJobColumns', JSON.stringify(preferences));
  } catch {
    // Ignore errors
  }
};

export const assignedColumnsReducer = createReducer(
  initialScheduledJobColumnState,
  on(schedulerActions.initAssColumnsAction, (state, { columns }) => {
    const savedPrefs = loadAssignedColumnPreferences();
    const savedVisibility = savedPrefs?.visibility;
    const savedOrder = savedPrefs?.order;

    if (columns.length > 0) {
      let result = initialScheduledJobColumnState
        .map((col, i) => {
          const pos = columns.findIndex((u) => u.fieldName === col.fieldName);
          return {
            ...col,
            // Prioritize localStorage preferences over server values
            isChecked: savedVisibility && col.fieldName in savedVisibility
              ? savedVisibility[col.fieldName]
              : (pos > -1 ? columns[pos].isChecked : col.isChecked),
            // ...and column order.
            position: pos > -1 ? pos : i + initialScheduledJobColumnState.length,
            width: pos > -1 ? columns[pos].width : -1, // todo: what is a good default?
          };
        });

      // If we have a saved order, apply it
      if (savedOrder && savedOrder.length > 0) {
        result = result.sort((a, b) => {
          const aIndex = savedOrder.indexOf(a.fieldName);
          const bIndex = savedOrder.indexOf(b.fieldName);
          // If both are in saved order, sort by saved order
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only one is in saved order, it comes first
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          // Otherwise, sort by position
          return a.position - b.position;
        });
      } else {
        result = result.sort((a, b) => (a.position > b.position ? 1 : -1));
      }

      return result;
    } else {
      return [...initialScheduledJobColumnState];
    }
  }),
  on(schedulerActions.toggleScheduledJobColumnAction, (state, { fieldName }) => {
    const newState = state.map((column) => {
      if (column.fieldName === fieldName) {
        return { ...column, isChecked: !column.isChecked };
      }
      return column;
    });
    saveAssignedColumnPreferences(newState);
    return newState;
  }),
  on(schedulerActions.reorderScheduledColumnAction, (state, { fieldName, position }) => {
    const from = state.findIndex((x) => x.fieldName === fieldName);
    if (from > -1) {
      const to = position;

      // Move from one position to another, shifting everything
      // as necessary.
      const copy = [...state];
      copy.splice(to, 0, copy.splice(from, 1)[0]);

      saveAssignedColumnPreferences(copy);
      return copy;
    }
    return state;
  }),

  on(schedulerActions.resetScheduledJobColumnsAction, (state) => {
    localStorage.removeItem('assignedJobColumns');
    return [...initialScheduledJobColumnState];
  }),
  on(schedulerActions.changeWidthScheduledJobColumnAction, (state, { fieldName, width }) => {
    const newState = state.map((column) => {
      if (column.fieldName === fieldName) {
        return { ...column, width };
      }
      return column;
    });
    saveAssignedColumnPreferences(newState);
    return newState;
  })
);

export const selectedJobsReducer = createReducer(
  [] as number[],
  on(schedulerActions.setJobSelectionAction, (state, { payload }) => {
    const changing = new Set(payload.jobIds);
    const without_payload = state.filter((x) => !changing.has(x));
    if (payload.selected) {
      return without_payload.concat(payload.jobIds);
    } else {
      return without_payload;
    }
  })
);

export const summarizedJobsReducer = createReducer(
  [] as number[],
  on(schedulerActions.setJobSummarizedAction, (state, { payload }) => {
    const changing = new Set(payload.jobIds);
    const without_payload = state.filter((x) => !changing.has(x));
    if (payload.selected) {
      return without_payload.concat(payload.jobIds);
    } else {
      return without_payload;
    }
  })
);

export type IFlights = {
  flightNumber: number;
  jobIds: number[];
};

export type JobsInFlightState = { flights: IFlights[]; fliers: number[] };

const initialJobsFlight: JobsInFlightState = { flights: [], fliers: [] };
export const jobsInFlightReducer = createReducer(
  initialJobsFlight,
  on(schedulerActions.setJobsInFlightAction, (state, { flightNumber, jobId }) => {
    let newIds = [];
    if (Array.isArray(jobId)) newIds = [...jobId];
    else newIds = [jobId];

    return {
      flights: [...state.flights, { flightNumber, jobIds: newIds }],
      fliers: [...state.fliers, ...newIds],
    };
  }),
  on(schedulerActions.landJobsInFlightAction, (state, { flightNumber }) => {
    const flights = state.flights.filter((x) => x.flightNumber !== flightNumber);
    return {
      flights: [...flights],
      fliers: flights.flatMap((x) => x.jobIds),
    };
  })
);
